import React from 'react';

import CommonImage from '@/components/CommonImage';
import CustomDropdown from '@/components/CustomDropdown';
import { useMixPanelTrakingEventMutation } from '@/api/commonApis';

import { COSTING_MIX_PANEL_EVENTS } from '../utils';

const shippingTypeOptions = [
	{ value: '%', shippingType: 'PERCENTAGE' },
	{ value: '$', shippingType: 'VALUE' }
];

const getShippingTypeSymbol = (shippingType) =>
	shippingTypeOptions.find((item) => item.shippingType === shippingType)?.value;

const FobDecisionPanel = ({
	fobDecisionDetail,
	handleFobDesision,
	similarTypeData,
	handleRetailRefClick,
	calculateNetValue,
	calculateNetRetailUsd,
	calculateTargetLandedCost,
	buyerMinRange,
	buyerMaxRange,
	suggestedFobPrice,
	handleFobSuggest,
	productionIsloading,
	isEmptyFobInput,
	handleContinueToProduction,
	usdConversionPrice,
	currency,
	shippingTypeItem,
	setShippingTypeItem
}) => {
	const [mixPanelTrakingEvent] = useMixPanelTrakingEventMutation();

	const trackFobCost = (type, value) => {
		mixPanelTrakingEvent({
			type,
			body: {
				price: value
			}
		});
	};

	const trackShippingCost = (value) => {
		trackFobCost(COSTING_MIX_PANEL_EVENTS.SHIPPING_COST_CLICK, value);
	};

	return (
		<div className="market-context-rightbar">
			<div className="costing-summary border p-3 range-input">
				<p className="semibold-16 gray_dark_01 mb-3">FOB Decision</p>
				<div>
					<div className="d-flex align-items-center justify-content-between mb-1">
						<label className="regular-12 gray_dark_02 mb-0" htmlFor="retail-reference">
							Gross Retail Price
						</label>
						<div className="costing-input-with-prefix position-relative">
							<span className="position-absolute currency-symbol regular-12 gray_dark_02">
								{currency}
							</span>
							<input
								id="retail-reference"
								type="number"
								value={
									fobDecisionDetail?.retailReferencePrice ??
									similarTypeData?.medianRetailPrice ??
									''
								}
								onChange={(e) => handleFobDesision('retailReferencePrice', e.target.value)}
							/>
						</div>
					</div>
					<button
						className="button retail-button text size32 d-flex align-items-center justify-content-between w-100 px-0 mb-1"
						type="button"
						onClick={() => {
							mixPanelTrakingEvent({
								type: COSTING_MIX_PANEL_EVENTS.SUGGESTED_MEDIAN_RETAIL_PRICE_CLICK,
								body: {
									price: similarTypeData?.medianRetailPrice
								}
							});
							handleRetailRefClick(similarTypeData?.medianRetailPrice);
						}}
					>
						<p></p>
						<span className="regular-12 blue text-underline">
							median retail ({similarTypeData?.medianRetailPrice})
						</span>
					</button>
					<div className="d-flex align-items-center justify-content-between mb-1">
						<label className="regular-12 gray_dark_02 mb-0" htmlFor="vat">
							Vat
						</label>
						<div className="costing-input-with-prefix position-relative">
							<span className="position-absolute right-2 top-2 regular-12 gray_dark_02">%</span>
							<input
								id="vat"
								type="number"
								value={fobDecisionDetail?.vatPercentage ?? ''}
								onChange={(e) => handleFobDesision('vatPercentage', e.target.value)}
								onBlur={(e) => {
									trackFobCost(COSTING_MIX_PANEL_EVENTS.VAT_CLICK, e.target.value);
								}}
							/>
						</div>
					</div>
					<div className="d-flex align-items-center justify-content-between mb-1">
						<label className="regular-12 gray_dark_02 mb-0" htmlFor="net-value">
							Net Retail Price ({currency})
						</label>
						<div className="costing-input-with-prefix with-bg position-relative">
							<span className="position-absolute currency-symbol regular-12 gray_dark_02">
								{currency}
							</span>
							<input
								id="net-value"
								type="number"
								value={
									fobDecisionDetail?.netValue ??
									calculateNetValue(
										fobDecisionDetail?.retailReferencePrice ??
											similarTypeData?.medianRetailPrice,
										fobDecisionDetail?.vatPercentage
									)
								}
								readOnly
							/>
						</div>
					</div>
					<div className="d-flex align-items-center justify-content-between mb-1">
						<label className="regular-12 gray_dark_02 mb-0" htmlFor="usd-con-reference">
							USD Conversion Rate
						</label>
						<div className="costing-input-with-prefix position-relative">
							<input
								id="usd-con-reference"
								type="number"
								value={
									fobDecisionDetail?.usdConversionRate ??
									fobDecisionDetail?.usdConReference ??
									usdConversionPrice ??
									''
								}
								onChange={(e) => handleFobDesision('usdConversionRate', e.target.value)}
								onBlur={(e) =>
									trackFobCost(
										COSTING_MIX_PANEL_EVENTS.USD_CONVERSION_RATE_CLICK,
										e.target.value
									)
								}
							/>
							<span className="position-absolute currency-symbol regular-12 gray_dark_02">
								$
							</span>
						</div>
					</div>
					<div className="d-flex align-items-center justify-content-between mb-1">
						<label className="regular-12 gray_dark_02 mb-0" htmlFor="net-retail-usd">
							Net Retail Price ($)
						</label>
						<div className="costing-input-with-prefix with-bg position-relative">
							<input
								id="net-retail-usd"
								type="number"
								value={
									fobDecisionDetail?.netRetailUsd ??
									calculateNetRetailUsd(
										fobDecisionDetail?.netValue,
										fobDecisionDetail?.usdConversionRate ??
											fobDecisionDetail?.usdConReference ??
											usdConversionPrice
									)
								}
								readOnly
							/>
							<span className="position-absolute currency-symbol regular-12 gray_dark_02">
								$
							</span>
						</div>
					</div>

					<div className="d-flex align-items-center justify-content-between mb-1">
						<label className="regular-12 gray_dark_02 mb-0" htmlFor="brand-margin">
							Brand / Retail Margin
						</label>
						<div className="costing-input-with-suffix position-relative">
							<input
								id="brand-margin"
								type="number"
								value={fobDecisionDetail?.retailPercentage ?? ''}
								onChange={(e) => handleFobDesision('retailPercentage', e.target.value)}
							/>
							<span className="position-absolute right-2 top-2 regular-12 gray_dark_02">%</span>
						</div>
					</div>
					<div className="d-flex align-items-center justify-content-between mb-1">
						<label className="regular-12 gray_dark_02 mb-0" htmlFor="target-landed-cost">
							Target Landed Cost
						</label>
						<div className="costing-input-with-suffix with-bg position-relative">
							<input
								id="target-landed-cost"
								type="number"
								value={
									fobDecisionDetail?.targetLandedCost ??
									calculateTargetLandedCost(
										fobDecisionDetail?.netRetailUsd,
										fobDecisionDetail?.retailPercentage
									)
								}
								readOnly
							/>
							<span className="position-absolute currency-symbol regular-12 gray_dark_02">
								$
							</span>
						</div>
					</div>

					<div className="d-flex align-items-center justify-content-between mb-1">
						<label className="regular-12 gray_dark_02 mb-0" htmlFor="freight-logistics">
							Shipping Cost
						</label>
						<div className="sipping-type-container d-flex align-items-center">
							<CustomDropdown
								isShowSearch={false}
								items={shippingTypeOptions}
								onItemClick={(item) => {
									setShippingTypeItem(item.shippingType);
									trackShippingCost(
										item.shippingType === 'VALUE'
											? fobDecisionDetail?.shippingValue
											: fobDecisionDetail?.shippingPercentage
									);
								}}
								selectedItem={getShippingTypeSymbol(shippingTypeItem)}
							/>
							{shippingTypeItem === 'VALUE' && (
								<div className="costing-input-with-suffix position-relative">
									<input
										id="freight-logistics"
										type="number"
										value={fobDecisionDetail?.shippingValue ?? ''}
										onChange={(e) => handleFobDesision('shippingValue', e.target.value)}
										onBlur={(e) => trackShippingCost(e.target.value)}
									/>
								</div>
							)}
							{shippingTypeItem === 'PERCENTAGE' && (
								<div className="costing-input-with-suffix position-relative">
									<input
										id="freight-logistics"
										type="number"
										value={fobDecisionDetail?.shippingPercentage ?? ''}
										onChange={(e) =>
											handleFobDesision('shippingPercentage', e.target.value)
										}
										onBlur={(e) => trackShippingCost(e.target.value)}
									/>
								</div>
							)}
						</div>
					</div>

					<div className="d-flex align-items-center justify-content-between mb-1">
						<label className="regular-12 gray_dark_02 mb-0" htmlFor="freight-warehouse">
							Freight to Warehouse
						</label>
						<div className="costing-input-with-suffix position-relative">
							<input
								id="freight-warehouse"
								type="number"
								value={fobDecisionDetail?.warehousePercentage ?? ''}
								onChange={(e) => handleFobDesision('warehousePercentage', e.target.value)}
								onBlur={(e) =>
									trackFobCost(
										COSTING_MIX_PANEL_EVENTS.WAREHOUSE_COST_CLICK,
										e.target.value
									)
								}
							/>
							<span className="position-absolute right-2 top-2 regular-12 gray_dark_02">%</span>
						</div>
					</div>

					<div className="d-flex align-items-center justify-content-between mb-1">
						<label className="regular-12 gray_dark_02 mb-0" htmlFor="duty-import-tax">
							Duty / Import Tax
						</label>
						<div className="costing-input-with-suffix position-relative">
							<input
								id="duty-import-tax"
								type="number"
								value={fobDecisionDetail?.dutyPercentage ?? ''}
								onChange={(e) => handleFobDesision('dutyPercentage', e.target.value)}
								onBlur={(e) =>
									trackFobCost(
										COSTING_MIX_PANEL_EVENTS.IMPORT_DUTY_COST_CLICK,
										e.target.value
									)
								}
							/>
							<span className="position-absolute right-2 top-2 regular-12 gray_dark_02">%</span>
						</div>
					</div>

					<div className="d-flex align-items-center justify-content-between mb-1">
						<label className="regular-12 gray_dark_02 mb-0" htmlFor="other-buyer-costs">
							Other Buyer Costs
						</label>
						<div className="costing-input-with-suffix position-relative">
							<input
								id="other-buyer-costs"
								type="number"
								value={fobDecisionDetail?.otherCostPercentage ?? ''}
								onChange={(e) => handleFobDesision('otherCostPercentage', e.target.value)}
								onBlur={(e) =>
									trackFobCost(COSTING_MIX_PANEL_EVENTS.OTHER_COST_CLICK, e.target.value)
								}
							/>
							<span className="position-absolute right-2 top-2 regular-12 gray_dark_02">%</span>
						</div>
					</div>

					<div className="border p-3 mb-2">
						<div className="d-flex align-items-center justify-content-between mb-3">
							<p className="regular-12 gray_dark_02 text-uppercase mb-0">Buyer buying range</p>
							<p className="semibold-14 gray_dark_01 mb-0">
								${buyerMinRange} - ${buyerMaxRange}
							</p>
						</div>
						<p className="regular-12 weight-600 gray_dark_01 mb-2">Your target FOB to offer </p>
						<input
							className="form-control mb-2"
							type="number"
							value={fobDecisionDetail?.targetedFobPrice ?? ''}
							onChange={(e) => handleFobDesision('targetedFobPrice', e.target.value)}
						/>
						<button
							className="button text size32 bg-gray px-2"
							type="button"
							onClick={() => {
								mixPanelTrakingEvent({
									type: COSTING_MIX_PANEL_EVENTS.SUGGESTED_FOB_PRICE_CLICK,
									body: {
										price: suggestedFobPrice
									}
								});
								handleFobSuggest(suggestedFobPrice);
							}}
						>
							<span className="regular-12 gray_dark_01 weight-500">
								Use suggested ${suggestedFobPrice || '0.00'}
							</span>
						</button>
					</div>
				</div>
				<button
					className={`button dark w-100 ${productionIsloading || isEmptyFobInput ? 'disabled cursor-none opacity-50' : ''}`}
					type="button"
					disabled={productionIsloading || isEmptyFobInput}
					onClick={() => {
						mixPanelTrakingEvent({
							type: COSTING_MIX_PANEL_EVENTS.PRODUCTION_COST_CLICK,
							body: {
								price: fobDecisionDetail?.targetedFobPrice
							}
						});
						handleContinueToProduction();
					}}
				>
					Continue to Production Costing →{' '}
					{productionIsloading && (
						<span className="rotated-icon icon-spin">
							<CommonImage src="/icons/Update-gray.svg" />
						</span>
					)}
				</button>
			</div>
		</div>
	);
};

export default FobDecisionPanel;
